import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'

import type { LoyaltyCard, LoyaltyCardStatusType } from '@/api/admin-loyalty-card-types'
import type { OrderListItem } from '@/api/admin-orders-types'
import { useDeleteLoyaltyCard } from '@/api/api-mutations'
import { preloadGetLoyaltyCard, preloadListOrders, useGetLoyaltyCard, useListOrders } from '@/api/react-query'
import { Container, ContainerHeader } from '@/components/base/container'
import { IconButton } from '@/components/base/icon-button'
import { LockedIconButton, managedExternallyLabel } from '@/components/base/managed-externally'
import { usePrompt } from '@/components/base/prompt'
import { SearchField } from '@/components/base/search-field'
import { SectionRow } from '@/components/base/section-row'
import { StatusCell, type StatusColor } from '@/components/base/status-cell'
import { toast } from '@/components/base/toast'
import { SingleColumnPage } from '@/components/page-layout'
import { DEFAULT_PAGE_SIZE, Pagination } from '@/components/table/pagination'
import { Table } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { PencilSquare } from '@/icons/pencil-square'
import { Trash } from '@/icons/trash'
import { ConditionalRouteComponent, PaginatedSearchSchema } from '@/utils/route-utilities'

import { useOrderListColumnDefs } from '../orders/-components'

export const Route = createFileRoute('/_authenticated/_general/loyalty-cards/$cardUuid')({
	component: () => (
		<ConditionalRouteComponent routePath={Route.id} pathsToRenderOn={['/edit']}>
			<LoyaltyCardDetail />
		</ConditionalRouteComponent>
	),
	validateSearch: PaginatedSearchSchema,
	loaderDeps: ({ search: { limit, offset, q } }) => ({ limit, offset, q }),
	loader: async ({ context, params: { cardUuid }, deps: { limit, offset, q } }) => {
		preloadListOrders(context, {
			query: { limit, offset, q, customerCardUuid: cardUuid }
		})
		await preloadGetLoyaltyCard(context, { path: { cardUuid } })
	},
	staticData: {
		crumb: ({ context: { L } }) => L.LoyaltyCards.Detail.Title()
	}
})

function LoyaltyCardDetail() {
	const { L } = useI18n()
	const { cardUuid } = Route.useParams()
	const { limit = DEFAULT_PAGE_SIZE, offset, q } = Route.useSearch()

	const card = useGetLoyaltyCard({ cardUuid })

	const orders = useListOrders({ limit, offset, q, customerCardUuid: cardUuid })
	const orderColumnDefs = useOrderListColumnDefs({ includePoints: true })

	const customerName = formatCustomerName(card.data?.customer, L.Customers.Anonymous())

	const navigate = useNavigate()
	const prompt = usePrompt()
	const deleteCard = useDeleteLoyaltyCard(true)

	const locked = managedExternallyLabel(
		card.data?.externalSource as 'PASSCREATOR' | undefined,
		L.Enum.CardExternalSource,
		L.LoyaltyCards.ManagedExternally
	)

	const handleDelete = async () => {
		const confirmed = await prompt({
			title: L.LoyaltyCards.Delete.Title(),
			description: L.LoyaltyCards.Delete.Description(),
			confirmText: L.General.Delete(),
			cancelText: L.General.Cancel()
		})
		if (!confirmed) return
		try {
			const result = await deleteCard.mutateAsync({ path: { cardUuid } })
			if (result.isSuccess) {
				toast.success(L.LoyaltyCards.DeleteSuccess())
				navigate({ to: '/loyalty-cards' })
			} else {
				toast.error((result.response as { message?: string })?.message ?? L.Error.SomethingWentWrong())
			}
		} catch (e: any) {
			toast.error(e.message)
		}
	}

	return (
		<SingleColumnPage hasOutlet>
			{/* General */}
			<Container className='divide-y'>
				<ContainerHeader
					heading={card.data?.name || L.LoyaltyCards.Detail.Title()}
					rightSide={
						locked ? (
							<LockedIconButton label={locked}>
								<PencilSquare />
							</LockedIconButton>
						) : (
							<Link from={Route.fullPath} to='edit'>
								<IconButton tooltipText={L.General.Edit()}>
									<PencilSquare />
								</IconButton>
							</Link>
						)
					}
					actions={[
						{
							actions: [
								{
									icon: <Trash />,
									label: L.General.Delete(),
									onClick: handleDelete,
									disabled: !!locked,
									disabledTooltip: locked
								}
							]
						}
					]}
				/>
				<SectionRow
					title={L.Fields.Status()}
					value={
						card.data?.status ? (
							<StatusCell color={getLoyaltyCardStatusColor(card.data.status)}>
								{L.Enum.CardStatus(card.data.status)}
							</StatusCell>
						) : (
							'-'
						)
					}
				/>
				{card.data?.customer ? (
					<Link to='/customers/$customerUuid' params={{ customerUuid: card.data.customer.uuid }}>
						<SectionRow title={L.LoyaltyCards.Detail.Customer()} value={customerName} />
					</Link>
				) : (
					<SectionRow title={L.LoyaltyCards.Detail.Customer()} value='-' />
				)}
			</Container>

			{/* Card Details */}
			<Container className='divide-y'>
				<ContainerHeader heading={L.LoyaltyCards.Detail.CardDetails()} />
				<SectionRow title={L.LoyaltyCards.Column.CardNumber()} value={card.data?.cardNumber ?? '-'} />
				<SectionRow
					title={L.LoyaltyCards.Column.AvailablePoints()}
					value={
						typeof card.data?.availablePoints === 'number'
							? card.data.availablePoints.toLocaleString()
							: '-'
					}
				/>
				<SectionRow
					title={L.LoyaltyCards.Column.TotalPoints()}
					value={typeof card.data?.totalPoints === 'number' ? card.data.totalPoints.toLocaleString() : '-'}
				/>
				<SectionRow title={L.LoyaltyCards.Column.ExternalId()} value={card.data?.externalId ?? '-'} />
				<SectionRow title={L.LoyaltyCards.Column.ExternalSource()} value={card.data?.externalSource ?? '-'} />
				<SectionRow
					title={L.Fields.CreatedAt()}
					value={card.data?.createdAt ? new Date(card.data.createdAt).toLocaleDateString() : '-'}
				/>
				<SectionRow
					title={L.Fields.UpdatedAt()}
					value={card.data?.updatedAt ? new Date(card.data.updatedAt).toLocaleDateString() : '-'}
				/>
			</Container>

			<Container className='flex flex-col'>
				<ContainerHeader heading={L.Orders.Title()} rightSide={<SearchField />} />
				<div className='min-w-0 overflow-x-auto'>
					<Table<OrderListItem>
						className='w-full min-w-[600px]'
						data={orders.data?.orders}
						columnUtilities={{
							getCurrencyCode: ({ currencyCode }) => currencyCode
						}}
						columnDefs={orderColumnDefs}
						animateRows
						suppressCellFocus
						domLayout='autoHeight'
						rowToRouterConfig={row => ({
							to: '/orders/$orderUuid',
							params: { orderUuid: row.uuid }
						})}
					/>
				</div>
				<div className='px-3 py-2 sm:px-6 sm:py-4'>
					<Pagination totalCount={orders.data?.count} />
				</div>
			</Container>
		</SingleColumnPage>
	)
}

const formatCustomerName = (customer: LoyaltyCard['customer'] | undefined, anonymousFallback: string): string => {
	if (!customer) return '-'
	const name = [customer.firstName, customer.lastName].filter(Boolean).join(' ')
	return name || customer.email || anonymousFallback
}

const getLoyaltyCardStatusColor = (status: LoyaltyCardStatusType): StatusColor => {
	switch (status) {
		case 'ACTIVE':
			return 'green'
		case 'INACTIVE':
			return 'grey'
		case 'BLOCKED':
			return 'orange'
		default:
			return 'grey'
	}
}
